import React, { lazy } from 'react';
import { BrainIcon, ListTodoIcon, ZapIcon, SunriseIcon, BellOffIcon, HeartCrackIcon } from 'lucide-react';
type PainPoint = {
  icon: React.ReactNode;
  title: string;
  description: string;
  glowColor: string;
};
export function ProblemSection() {
  return <section id="problem" className="bg-[#0F0F1A] py-24 px-6" data-id="element-176">
      <div className="max-w-6xl mx-auto" data-id="element-177">
        {/* Section header */}
        <div className="text-center mb-16" data-id="element-178">
          <p className="text-[#4361EE] text-sm font-bold tracking-[0.3em] uppercase mb-4" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-179">
            You're not broken. You're just unoptimized.
          </p>
          <h2 className="text-[64px] sm:text-[80px] lg:text-[96px] text-white leading-none" style={{
          fontFamily: "'Bebas Neue', sans-serif"
        }} data-id="element-180">
            DOES THIS SOUND{' '}
            <span className="text-[#F72585] glow-text-pink" data-id="element-181">FAMILIAR?</span>
          </h2>
        </div>

        {/* Pain point grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-id="element-182">
          <PainCard icon={<ListTodoIcon className="w-7 h-7" data-id="element-184" />} title="You start 10 tasks and finish zero" description="Every day begins with ambition and ends with a graveyard of half-done projects." glowColor="rgba(67, 97, 238, 0.6)" accentColor="#4361EE" data-id="element-183" />
          <PainCard icon={<BrainIcon className="w-7 h-7" data-id="element-186" />} title="Your to-do list is a graveyard" description="You've tried every app, every system, every hack. Nothing sticks longer than a week." glowColor="rgba(247, 37, 133, 0.6)" accentColor="#F72585" data-id="element-185" />
          <PainCard icon={<ZapIcon className="w-7 h-7" data-id="element-188" />} title="You hyperfocus on the wrong things" description="Hours vanish into rabbit holes while the actual important work sits untouched." glowColor="rgba(67, 97, 238, 0.6)" accentColor="#4361EE" data-id="element-187" />
          <PainCard icon={<SunriseIcon className="w-7 h-7" data-id="element-190" />} title="Mornings feel impossible" description="Getting started is the hardest part. By the time you're warmed up, the day is half gone." glowColor="rgba(247, 37, 133, 0.6)" accentColor="#F72585" data-id="element-189" />
          <PainCard icon={<BellOffIcon className="w-7 h-7" data-id="element-192" />} title="You forget everything important" description="Appointments, deadlines, promises — your brain drops them the moment they're out of sight." glowColor="rgba(67, 97, 238, 0.6)" accentColor="#4361EE" data-id="element-191" />
          <PainCard icon={<HeartCrackIcon className="w-7 h-7" data-id="element-194" />} title="You feel broken, not lazy" description="You know you're capable. You just can't seem to access that version of yourself consistently." glowColor="rgba(247, 37, 133, 0.6)" accentColor="#F72585" data-id="element-193" />
        </div>

        {/* Bottom callout */}
        <div className="text-center" data-id="element-195">
          <div className="inline-block border border-[#4361EE]/40 rounded-2xl px-8 py-6" style={{
          background: 'rgba(67, 97, 238, 0.05)'
        }} data-id="element-196">
            <p className="text-white text-xl sm:text-2xl font-bold leading-relaxed" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-197">
              If you nodded at even{' '}
              <span className="text-[#FFD60A] glow-text-yellow" data-id="element-198">ONE</span> of
              these —
              <br data-id="element-199" />
              <span className="text-[#4361EE]" data-id="element-200">this was built for you.</span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}
type PainCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  glowColor: string;
  accentColor: string;
};
function PainCard({
  icon,
  title,
  description,
  glowColor,
  accentColor
}: PainCardProps) {
  return <div className="group relative rounded-xl p-6 border border-white/5 transition-all duration-300 cursor-default" style={{
    background: 'linear-gradient(135deg, #0F3460 0%, #0F0F1A 100%)'
  }} onMouseEnter={e => {
    e.currentTarget.style.boxShadow = `0 0 30px ${glowColor}, 0 0 60px ${glowColor.replace('0.6', '0.2')}`;
    e.currentTarget.style.borderColor = accentColor + '60';
  }} onMouseLeave={e => {
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
  }} data-id="element-201">
      {/* Icon */}
      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
      background: accentColor + '20',
      color: accentColor
    }} data-id="element-202">
        {icon}
      </div>

      {/* Title */}
      <h3 className="text-white font-bold text-lg mb-2 leading-tight" style={{
      fontFamily: "'Inter', sans-serif"
    }} data-id="element-203">
        {title}
      </h3>

      {/* Description */}
      <p className="text-[#A0AEC0] text-sm leading-relaxed" style={{
      fontFamily: "'Inter', sans-serif"
    }} data-id="element-204">
        {description}
      </p>

      {/* Accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-b-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
      background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`
    }} data-id="element-205" />
    </div>;
}